/*!
 * v-resize-observer v2.0.2
 * (c) 2023 Mervin<mengqing723@gmail.com>
 * Released under the MIT License.
 */
import ResizeObserver from 'resize-observer-polyfill';
import { isVue2, defineComponent, watch, onMounted, getCurrentInstance, onBeforeUnmount, h } from 'vue-demi';

// 简单的防抖函数
// import debounce from 'lodash/debounce'
function debounce(func, delay) {
    var timer;
    return function () {
        var _this = this;
        clearTimeout(timer);
        var args = arguments;
        timer = setTimeout(function () {
            func.apply(_this, args);
        }, delay);
    };
}
var Resizer = /** @class */ (function () {
    function Resizer(el, options) {
        this.resizeObserver = null;
        this.width = 0;
        this.height = 0;
        var $el = el;
        if (typeof el === 'string') {
            $el = document.querySelector(el);
        }
        this.element = $el;
        this.options = Object.assign({
            delay: 150
        }, options);
        this.observe(this.element);
    }
    Resizer.prototype.observe = function (element) {
        if (element) {
            this.element = element;
        }
        else {
            element = this.element;
        }
        if (!(element instanceof HTMLElement)) {
            throw new Error('The target element must be a HTMLElement');
        }
        var _a = element.getBoundingClientRect(), width = _a.width, height = _a.height;
        this.width = Math.floor(width);
        this.height = Math.floor(height);
        if (typeof this.options.resize === 'function' && this.options.immediate) {
            this.options.resize({ width: width, height: height }, element);
        }
        if (this.resizeObserver) {
            this.disconnect();
        }
        this.resizeObserver = new ResizeObserver(this._onResize());
        this.resizeObserver.observe(element);
    };
    Resizer.prototype._onResize = function () {
        var delay = this.options.delay || this.options.wait;
        if (delay) {
            return debounce(this._handleResize.bind(this), delay);
        }
        return this._handleResize.bind(this);
    };
    Resizer.prototype._handleResize = function (entries) {
        for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
            var entry = entries_1[_i];
            var target = entry.target;
            var _a = target.getBoundingClientRect(), width = _a.width, height = _a.height;
            width = Math.floor(width);
            height = Math.floor(height);
            if (this.width !== width || this.height !== height) {
                this.width = width;
                this.height = height;
                if (typeof this.options.resize === 'function') {
                    this.options.resize({ width: width, height: height }, target);
                }
            }
        }
    };
    Resizer.prototype.disconnect = function () {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    };
    Resizer.prototype.destroy = function () {
        this.disconnect();
        this.element = null;
    };
    return Resizer;
}());

function getOptions(_a) {
    var value = _a.value, arg = _a.arg, modifiers = _a.modifiers;
    var options = {};
    if (typeof value === 'function') {
        options.resize = value;
    }
    options.delay = isNaN(parseInt(arg)) ? 150 : parseInt(arg);
    options.immediate = !!modifiers.immediate;
    return options;
}
var directive = {
    //! 这里使用 `inserted` 钩子，表示元素被插入父节点中
    inserted: function (el, binding) {
        var value = binding.value;
        if (value && typeof value !== 'function') {
            return console.warn('v-resize should received a function as value');
        }
        var ro = new Resizer(el, getOptions(binding));
        el.__vue_resize__ = ro;
    },
    unbind: function (el) {
        var ro = el.__vue_resize__;
        if (ro) {
            ro.destroy();
        }
    }
};
var resizeDirective = isVue2 ? directive : {
    mounted: directive.inserted,
    beforeUnmount: directive.unbind
};

var ResizeComponent = defineComponent({
    name: 'ResizeObserverComponent',
    props: {
        target: [String, Object],
        disabled: Boolean,
        immediate: Boolean,
        delay: {
            type: Number,
            default: 150
        }
    },
    setup: function (props, _a) {
        var _b;
        var slots = _a.slots;
        var ro = null;
        watch(function () { return props.disabled; }, function (val) {
            if (val) {
                ro === null || ro === void 0 ? void 0 : ro.disconnect();
            }
            else {
                ro === null || ro === void 0 ? void 0 : ro.observe();
            }
        });
        onMounted(function () {
            var _a;
            // const { target, disabled, limiter, wait } = toRefs(props)
            var vm = (_a = getCurrentInstance()) === null || _a === void 0 ? void 0 : _a.proxy;
            var $el = vm === null || vm === void 0 ? void 0 : vm.$el;
            if (props.target) {
                if (typeof props.target === 'string') {
                    $el = document.querySelector(props.target);
                }
                else if (props.target instanceof HTMLElement) {
                    $el = props.target;
                }
            }
            var options = {
                delay: props.delay,
                immediate: props.immediate,
                resize: function (data, elem) {
                    vm === null || vm === void 0 ? void 0 : vm.$emit('resize', data, elem);
                }
            };
            ro = new Resizer($el, options);
        });
        // 销毁 ResizeObserver实例
        onBeforeUnmount(function () {
            if (ro) {
                ro.destroy();
            }
        });
        var defaultContent = (_b = slots.default) === null || _b === void 0 ? void 0 : _b.call(slots);
        return function () { return ((defaultContent && defaultContent.length === 1)
            ? defaultContent[0]
            : h('div', defaultContent)); };
    }
});

var install = function (app, options) {
    if (options === void 0) { options = {}; }
    if (install.installed)
        return;
    install.installed = true;
    app.directive(options.directive || 'resize', resizeDirective);
    app.component(options.component || 'ResizeComponent', ResizeComponent);
};
var index = {
    install: install,
    ResizeObserver: Resizer,
    ResizeComponent: ResizeComponent,
    resizeDirective: resizeDirective,
    directive: resizeDirective,
    component: ResizeComponent
};

export { ResizeComponent, Resizer as ResizeObserver, index as default, resizeDirective };
